import React, { FC, useContext, useState, useEffect } from "react";
import { Box, Button, ListItemText, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import theme from "common/theme/theme";
import useProjectStore, { INetworkContainer } from "common/store/ProjectStore";
import { LanguageContext } from "common/contexts/LanguageContext";
import BladeIconButton from "./BladeIconButton";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";
import DialogTextField from "common/components/dialogs/DialogTextField";
import { ValidInputCharsRegEx } from "common/constants/GlobalValues";
import useNotificationStore from "common/store/NotificationStore";
import { SharedProjectContext } from "components/project/Project";

interface IProps {
  container: INetworkContainer;
}

const NetworkContainerListItem: FC<IProps> = (props) => {
  const { container } = props;
  const [anchorActionsEl, setAnchorActionsEl] = useState<HTMLButtonElement | null>(null);
  const [actionsOpen, setActionsOpen] = useState<boolean>(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [isRenameOpen, setIsRenameOpen] = useState<boolean>(false);
  const [containerName, setContainerName] = useState<string>("");

  const {
    activeNetworkContainer,
    setActiveNetworkContainer,
    updateNetworkContainer,
    deleteNetworkContainer,
    setNetworkVisiblity,
  } = useProjectStore();
  const { getString } = useContext(LanguageContext);
  const { enqueueNotification } = useNotificationStore();
  const { setIsBusyBackgroundActive, setNetworkDeleted } = useContext(SharedProjectContext);

  useEffect(() => {
    setIsBusyBackgroundActive(isConfirmOpen || isRenameOpen);
  }, [isConfirmOpen, isRenameOpen, setIsBusyBackgroundActive])

  const handleOnClickSelectActiveNetworkContainer = (networkContainerID: number) => {
    setActiveNetworkContainer(networkContainerID, true);
  };

  const handleOnClickToggleVisibility = () => {
    container.networks.forEach((net) => {
      setNetworkVisiblity(net.networkID, !container.metaData.isVisible);
    });
  };

  const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorActionsEl(event.currentTarget);
    setActionsOpen(true);
  };

  const handleActionsClose = () => {
    setActionsOpen(false);
  };

  const handleDeleteMenuClick = () => {
    setActionsOpen(false);
    setIsConfirmOpen(true);
  };

  const handleRenameMenuClick = () => {
    setContainerName(container.networkContainerName);
    setActionsOpen(false);
    setIsRenameOpen(true);
  };

  const handleDeleteConfirmation = async () => {
    setIsConfirmOpen(false);
    deleteNetworkContainer(container.networkContainerID);
    setNetworkDeleted(true);
  };

  const handleRenameConfirmation = async () => {
    if (!ValidInputCharsRegEx.test(containerName)) {
      enqueueNotification("invalid_chars", `${getString('msg_invalid_chars')} ${getString('NetworkName')}`, "error");
      return;
    }
    setIsRenameOpen(false);
    updateNetworkContainer(container.networkContainerID, containerName);
  };

  return (
    <>
      <ListItemText
        sx={{
          backgroundColor: container.networkContainerID === activeNetworkContainer?.networkContainerID ? theme.accent.medium : theme.palette.common.white,
          color: theme.palette.common.black,
          p: "8px",
        }}
        primaryTypographyProps={{ fontSize: "12px" }}
      >
        <Box display="flex" justifyContent="space-between">
          <Box display="flex">
            {container === activeNetworkContainer ? (
              <CheckOutlinedIcon sx={{ width: "16px", height: "16px" }} />
            ) : (
              <BladeIconButton
                disableRipple
                sx={{ p: 0 }}
                onClick={handleOnClickToggleVisibility}
              >
                {container.metaData.isVisible ? (
                  <Tooltip title={getString("tt_hide_network")}>
                    <VisibilityOutlinedIcon sx={{ width: "16px", height: "16px" }} />
                  </Tooltip>
                ) : (
                  <Tooltip title={getString("tt_show_network")}>
                    <VisibilityOffOutlinedIcon sx={{ width: "16px", height: "16px" }} />
                  </Tooltip>
                )}
              </BladeIconButton>
            )}
            <Button
              variant="text"
              disableRipple
              sx={{
                padding: 0,
                color: theme.palette.common.black,
                "&:hover": {
                  color: theme.palette.primary.light,
                },
              }}
              onClick={() => handleOnClickSelectActiveNetworkContainer(container.networkContainerID)}
            >
              <Typography variant="innerLevelBlade" sx={{ padding: "2px" }}>
                {container.networkContainerName}
              </Typography>
            </Button>
          </Box>
          <BladeIconButton
            disableRipple
            onClick={handleActionsClick}
            sx={{ p: 0, ml: "8px", color: theme.palette.common.black, align: "right" }}
          >
            <MoreHorizIcon sx={{ width: "16px", height: "16px" }} />
          </BladeIconButton>
          <Menu anchorEl={anchorActionsEl} open={actionsOpen} onClose={handleActionsClose}>
            <MenuItem onClick={() => handleDeleteMenuClick()}>{getString("cmn_delete")}</MenuItem>
            <MenuItem onClick={() => handleRenameMenuClick()}>{getString("cmn_rename")}</MenuItem>
          </Menu>
        </Box>
      </ListItemText>
      <ConfirmationModal
        isOpen={isConfirmOpen}
        handleClose={() => {
          setIsConfirmOpen(false);
        }}
        handleConfirm={handleDeleteConfirmation}
        title={getString("dlg_del_nwk_title")}
        maxWidth="sm"
        hideDivider={true}
      >
        <Typography variant="subtitle1" sx={{ pt: "24px" }}>
          {getString("dlg_del_nwk_confirm")} '{container.networkContainerName}'?
        </Typography>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={isRenameOpen}
        handleClose={() => {
          setIsRenameOpen(false);
        }}
        handleConfirm={handleRenameConfirmation}
        title={getString("dlg_rename_nwk_title")}
        maxWidth="sm"
        hideDivider={true}
      >
        <DialogTextField
          sx={{ pt: "24px" }}
          variant="filled"
          size="small"
          label={getString("dlg_rename_nwk_name_lbl")}
          value={containerName}
          onChange={(e) => setContainerName(e.target.value)}
        />
      </ConfirmationModal>
    </>
  );
};

export default NetworkContainerListItem;
